import { Checkbox, CheckboxField, CheckboxGroup } from '../../catalyst/checkbox'
import { Fieldset, FieldGroup, Legend, Label } from '../../catalyst/fieldset'
import { Text } from '../../catalyst/text'
import { dropNulls } from '../../lib/api'
import { findingLabel, PEST_LOOKUP, pestLookupHref, SCOUTING_FINDINGS, type ScoutingFinding } from '../../lib/scouting'
import { ChoiceButtons } from '../chips'
import { optText, str, type FlowDef } from '../flow'
import type { Values } from '../use-draft'
import { rememberWho, WhoField } from '../who'
import { placeOf, TextField } from './shared'

// A scouting walk: what the grower saw on each field. The grower names the pest; Farmula never
// identifies one for them (PRODUCT.md §9.5). Once it is recorded, a pest links to Lookup by use case:
// the products registered against it (registered options, not a recommendation).

const SEVERITIES = [
  { value: 'low', label: 'A little' },
  { value: 'medium', label: 'Some' },
  { value: 'high', label: 'A lot' },
] as const

const findingsOf = (v: Values): ScoutingFinding[] => (Array.isArray(v.findings) ? (v.findings as ScoutingFinding[]) : [])

export const scoutingFlow: FlowDef = {
  kind: 'scouting',
  label: 'Scouted',
  needsField: true,
  severalFields: true,
  initial: ({ today, fieldId }) => ({ fieldId, date: today, findings: [] }),
  steps: [
    {
      id: 'seen',
      title: 'What did you see?',
      // Nothing found is worth recording too; "something else" needs its words.
      valid: (v) => !findingsOf(v).includes('other') || !!optText(v.otherFinding),
      render: (ctx) => {
        const findings = findingsOf(ctx.values)
        const toggle = (f: ScoutingFinding, on: boolean) => ctx.patch({ findings: on ? [...findings, f] : findings.filter((x) => x !== f) })
        return (
          <FieldGroup>
            <Fieldset>
              <Legend>Tick what you found</Legend>
              <CheckboxGroup className="mt-3">
                {SCOUTING_FINDINGS.map((f) => (
                  <CheckboxField key={f.value} className="rounded-lg px-4 py-3 ring-1 ring-zinc-950/10 dark:ring-white/10">
                    <Checkbox checked={findings.includes(f.value)} onChange={(on) => toggle(f.value, on)} />
                    <Label>{f.label}</Label>
                  </CheckboxField>
                ))}
              </CheckboxGroup>
              {findings.length === 0 && <Text className="mt-3">Nothing ticked: recorded as a clean walk.</Text>}
            </Fieldset>
            {findings.includes('other') && <TextField ctx={ctx} label="What else?" valueKey="otherFinding" />}
            {findings.length > 0 && (
              <ChoiceButtons label="How much? (optional)" value={str(ctx.values.severity) as (typeof SEVERITIES)[number]['value'] | ''} options={SEVERITIES} onChange={(s) => ctx.patch({ severity: s })} />
            )}
            <TextField ctx={ctx} label="Notes (optional)" valueKey="notes" multiline />
            <WhoField ctx={ctx} label="Who scouted?" valueKey="observedBy" />
          </FieldGroup>
        )
      },
    },
  ],
  review: (ctx) => {
    const { values } = ctx
    const findings = findingsOf(values)
    return {
      title: `Scouting${placeOf(ctx)}`,
      lines: [
        { label: 'Date', value: str(values.date) },
        { label: 'Found', value: findings.length ? findings.map((f) => (f === 'other' ? str(values.otherFinding) || 'something else' : findingLabel(f))).join(', ') : 'Nothing' },
        ...(optText(values.severity) ? [{ label: 'How much', value: SEVERITIES.find((s) => s.value === values.severity)?.label ?? str(values.severity) }] : []),
        ...(optText(values.notes) ? [{ label: 'Notes', value: str(values.notes) }] : []),
        ...(optText(values.observedBy) ? [{ label: 'Scouted by', value: str(values.observedBy) }] : []),
      ],
    }
  },
  entry: ({ values }) => {
    const findings = findingsOf(values)
    return dropNulls({
      fieldId: str(values.fieldId),
      observedOn: str(values.date),
      findings,
      otherFinding: findings.includes('other') ? (optText(values.otherFinding) ?? null) : null,
      severity: findings.length ? (optText(values.severity) ?? null) : null,
      notes: optText(values.notes) ?? null,
      observedBy: optText(values.observedBy) ?? null,
    })
  },
  historyPath: (farmId, fieldId) => `/farms/${farmId}/fields/${fieldId}/scouting-observations`,
  onRecorded: rememberWho('observedBy'),
  after: ({ values }) =>
    findingsOf(values).flatMap((f) => {
      const href = pestLookupHref(f)
      return href ? [{ label: `See products registered for ${PEST_LOOKUP[f]!.short} on blueberry`, href }] : []
    }),
}
